import { BillingBanner } from './billing-banner.js';
import { Button, cn, Icon, ThemeToggle, type IconName } from '@hvac/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { sessionQuery, setActiveOrganization } from '../../shared/api/queries.js';
import { DeletionBanner } from './deletion-banner.js';
import { useAuth } from '../../shared/auth/auth-context.js';
import { CommandPalette } from './command-palette.js';
import { GlobalDropzone } from './global-dropzone.js';

/**
 * The shell: a quiet sidebar, a generous canvas, and ⌘K.
 *
 * Navigation is grouped by the shape of a working day, what needs me, the
 * work itself, the business around it, never by database table. Two levels
 * maximum, every feature two clicks away, and the sidebar exists to be
 * forgotten: the intended muscle memory is ⌘K.
 *
 * On small screens the sidebar becomes a top strip rather than a drawer.
 * A drawer hides navigation behind a tap; a strip keeps the whole map visible,
 * and this app has seven destinations, not seventy.
 */

const NAV_GROUPS: readonly {
  label: string;
  items: readonly { to: string; label: string; icon: IconName; exact?: boolean }[];
}[] = [
  {
    label: 'Today',
    items: [
      { to: '/', label: 'Pulse', icon: 'pulse', exact: true },
      { to: '/everything', label: 'Everything', icon: 'search' },
      { to: '/timeline', label: 'Timeline', icon: 'clock' },
    ],
  },
  {
    label: 'Work',
    items: [
      { to: '/catch-up', label: 'Catch up', icon: 'check' },
      { to: '/review', label: 'Review', icon: 'review' },
      { to: '/bookkeeping', label: 'Bookkeeping', icon: 'bookkeeping' },
      { to: '/documents', label: 'Inbox', icon: 'documents' },
      { to: '/capture', label: 'Capture', icon: 'drop' },
      { to: '/questions', label: 'Questions', icon: 'pencil' },
    ],
  },
  {
    label: 'Business',
    items: [
      { to: '/suppliers', label: 'Suppliers', icon: 'suppliers' },
      { to: '/customers', label: 'Customers', icon: 'customer' },
      { to: '/jobs', label: 'Jobs', icon: 'job' },
      { to: '/invoices', label: 'Invoices', icon: 'documents' },
      { to: '/payroll', label: 'Payroll', icon: 'customer' },
      { to: '/vehicles', label: 'Vehicles', icon: 'equipment' },
      { to: '/equipment', label: 'Equipment', icon: 'equipment' },
      { to: '/bank', label: 'Bank', icon: 'bank' },
      { to: '/close', label: 'Close', icon: 'clock' },
      { to: '/reports', label: 'Reports', icon: 'chart' },
      { to: '/books', label: 'Books', icon: 'bookkeeping' },
      { to: '/tax', label: 'Tax', icon: 'tax' },
      { to: '/export', label: 'Export', icon: 'export' },
      { to: '/settings', label: 'Settings', icon: 'shield' },
    ],
  },
];

function NavLink({
  to,
  label,
  icon,
  exact,
}: {
  to: string;
  label: string;
  icon: IconName;
  exact?: boolean | undefined;
}) {
  return (
    <Link
      to={to}
      {...(exact === true ? { activeOptions: { exact: true } } : {})}
      className={cn(
        'flex items-center gap-2.5 rounded-[var(--radius-control)] px-3 py-2 text-sm text-white/60',
        'transition-all duration-[var(--duration-quick)] hover:bg-white/[0.06] hover:text-white',
        '[&.active]:bg-white/[0.08] [&.active]:font-medium [&.active]:text-white',
        '[&.active]:shadow-[inset_0_0_0_1px_rgb(96_165_250/0.25),0_0_16px_-4px_rgb(37_99_235/0.5)]',
        '[&.active_svg]:text-brand-400',
      )}
    >
      <Icon name={icon} className="text-white/40 transition-colors" />
      {label}
    </Link>
  );
}

export function AppLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { data: session } = useSuspenseQuery(sessionQuery());
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const handleSignOut = async (): Promise<void> => {
    await auth.signOut();
    await navigate({ to: '/login', search: {} });
  };

  // An account with no organisation gets told so once, here, instead of every
  // page firing requests at /organizations//… and surfacing routing errors.
  // The one page that must stay reachable is the one that fixes the situation.
  if (session.activeOrganizationId === null && pathname !== '/organizations/new') {
    return (
      <div className="flex min-h-dvh items-center justify-center px-6">
        <div className="max-w-md text-center">
          <p className="text-lg font-semibold text-content">Set up the first business.</p>
          <p className="mt-2 text-sm text-content-muted">
            You are signed in as {session.user.email}. Add a business to start its books, or, if
            someone is meant to invite you, open the link they send.
          </p>
          <div className="mt-6 flex justify-center gap-2">
            <Button onClick={() => void navigate({ to: '/organizations/new' })}>
              Set up a business
            </Button>
            <Button variant="secondary" onClick={() => void handleSignOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[15.5rem_1fr]">
      <CommandPalette
        organizationId={session.activeOrganizationId ?? ''}
        organizations={session.organizations.map((entry) => ({
          id: entry.organization.id,
          name: entry.organization.name,
        }))}
      />
      <GlobalDropzone />

      {/* The sidebar: near-black, floating, rounded, it frames the canvas
          rather than fencing it. */}
      <aside
        className={cn(
          'bg-gradient-to-b from-sidebar to-sidebar-deep text-white',
          'md:sticky md:top-3 md:m-3 md:flex md:h-[calc(100dvh-1.5rem)] md:flex-col',
          'md:rounded-[var(--radius-overlay)] md:shadow-[var(--shadow-raised)]',
          'md:ring-1 md:ring-inset md:ring-white/[0.06]',
        )}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-4 md:block">
          <div className="flex min-w-0 items-center gap-3">
            {/* The monogram: one letter of identity, so twelve clients in a
                switcher are told apart at a glance before a name is read. */}
            <span className="flex size-9 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]">
              {(
                session.organizations.find(
                  (entry) => entry.organization.id === session.activeOrganizationId,
                )?.organization.name ?? 'H'
              )
                .charAt(0)
                .toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              {/* The client switcher. One business reads as a title; the select
                is still there so "Add a business" is always one click away. */}
              <select
                aria-label="Business"
                value={session.activeOrganizationId ?? '__new'}
                onChange={(event) => {
                  if (event.target.value === '__new') {
                    void navigate({ to: '/organizations/new' });
                    return;
                  }
                  setActiveOrganization(event.target.value);
                  // A different tenant invalidates every cached answer.
                  window.location.assign('/');
                }}
                className={cn(
                  'w-full cursor-pointer appearance-none truncate border-0 bg-transparent p-0',
                  'text-sm font-semibold tracking-tight text-white focus:outline-none',
                )}
              >
                {session.organizations.map((entry) => (
                  <option
                    key={entry.organization.id}
                    value={entry.organization.id}
                    className="text-content"
                  >
                    {entry.organization.name}
                  </option>
                ))}
                <option value="__new" className="text-content">
                  ＋ Add a business…
                </option>
              </select>
              <p className="truncate text-xs text-white/40">{session.user.email}</p>
            </div>
          </div>

          {/* The search affordance doubles as the shortcut lesson. */}
          <button
            type="button"
            onClick={() =>
              window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))
            }
            className={cn(
              'mt-0 flex items-center justify-between gap-3 rounded-[var(--radius-control)]',
              'border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-white/50',
              'transition-all duration-[var(--duration-quick)]',
              'hover:border-white/20 hover:bg-white/[0.07] hover:text-white/80 md:mt-4 md:w-full',
            )}
          >
            <span>Search</span>
            <kbd className="rounded border border-white/15 px-1 font-sans text-white/40">⌘K</kbd>
          </button>
        </div>

        <nav className="flex gap-4 overflow-x-auto px-4 pb-3 md:flex-col md:gap-5 md:overflow-visible md:pb-0 md:pt-2">
          {(session.organizations.length > 1
            ? [
                {
                  label: 'Firm',
                  items: [{ to: '/clients', label: 'Clients', icon: 'clients' as const }],
                },
                ...NAV_GROUPS,
              ]
            : NAV_GROUPS
          ).map((group) => (
            <div key={group.label} className="flex shrink-0 items-center gap-1 md:block">
              <p className="hidden px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-white/30 md:block">
                {group.label}
              </p>
              <div className="flex gap-1 md:flex-col">
                {group.items.map((item) => (
                  <NavLink key={item.to} {...item} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="hidden md:mt-auto md:block md:px-4 md:py-4">
          <div className="mb-3 rounded-[var(--radius-control)] border border-dashed border-white/15 bg-white/[0.03] px-3 py-2.5">
            <p className="text-xs leading-snug text-white/45">
              <span className="font-medium text-white/70">Drop a file anywhere.</span> It sorts
              itself.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start px-2 text-white/50 hover:bg-white/[0.06] hover:text-white"
              onClick={() => void handleSignOut()}
            >
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      <main className="min-w-0 px-6 py-8 md:px-12 md:py-12">
        {/* Keyed on the path: each page arrives with the same quiet rise. */}
        <div key={pathname} className="animate-rise mx-auto max-w-6xl">
          <DeletionBanner organizationId={session.activeOrganizationId ?? ''} />
          <BillingBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
